// Empire Apps Chassis · connectors/health/fitbit-web
// Fitbit Web API v1 OAuth 2.0 (PKCE).
// Scopes: activity, heartrate, sleep, weight, nutrition.

import {
  type ConnectorAdapter,
  type ConnectorMode,
  type ConnectorResult,
  type ConnectorStatus,
  devModeEnabled,
  err,
  fetchWithBudget,
  liveDisabled,
  nowIso,
  ok,
  shortHash,
} from './_types.ts';

export interface FitbitDay {
  readonly date: string;
  readonly steps: number;
  readonly restingHeartRate: number;
  readonly sleepMinutes: number;
  readonly sleepEfficiency: number;
  readonly weightKg: number | null;
}

export interface FitbitPayload {
  readonly daysTrailing30: readonly FitbitDay[];
  readonly weeklyHrvMs: number | null;
}

export interface FitbitStatus extends ConnectorStatus {
  readonly id: 'fitbit-web';
}

export class FitbitConnector implements ConnectorAdapter<FitbitStatus, FitbitPayload> {
  readonly id = 'fitbit-web';
  readonly displayName = 'Fitbit';
  readonly displayNameHi = 'Fitbit · steps + sleep + heart';
  readonly dpdpCategory = 'PHI-medium · activity + sleep + heart';
  readonly mode: ConnectorMode;
  private connected = false;
  private lastSync: string | null = null;
  private principalHash: string | null = null;
  private accessToken: string | null = null;

  constructor(
    private readonly principalId: string,
    private readonly clientId: string | null = null,
  ) {
    this.mode = devModeEnabled() && this.clientId !== null ? 'live' : 'test';
  }

  async getStatus(): Promise<FitbitStatus> {
    if (!this.principalHash) this.principalHash = await shortHash(this.principalId);
    return {
      id: this.id,
      mode: this.mode,
      connected: this.connected,
      lastSyncAt: this.lastSync,
      principalIdHash: this.principalHash,
      notes: this.connected ? 'OAuth granted' : 'Fitbit sign-in pending',
    };
  }

  async connect(): Promise<ConnectorResult<FitbitStatus>> {
    if (this.mode === 'live') {
      if (!this.clientId) return err(liveDisabled(this.id));
      return err(liveDisabled(this.id));
    }
    this.connected = true;
    this.lastSync = nowIso();
    return ok(await this.getStatus());
  }

  setAccessToken(token: string): void {
    this.accessToken = token;
    this.connected = true;
    this.lastSync = nowIso();
  }

  async disconnect(): Promise<void> {
    this.accessToken = null;
    this.connected = false;
    this.lastSync = null;
  }

  async importPayload(): Promise<ConnectorResult<FitbitPayload>> {
    if (this.mode === 'live' && this.accessToken) {
      try {
        const days = await this.fetchDays();
        return ok({ daysTrailing30: days, weeklyHrvMs: null });
      } catch (e) {
        return err({ code: 'network', message: String(e instanceof Error ? e.message : e), retriable: true });
      }
    }
    return ok(this.mockPayload());
  }

  private async fetchDays(): Promise<readonly FitbitDay[]> {
    const today = new Date().toISOString().slice(0, 10);
    const stepsRes = await fetchWithBudget(
      `https://api.fitbit.com/1/user/-/activities/steps/date/today/30d.json`,
      { method: 'GET', headers: { Authorization: `Bearer ${this.accessToken}` } },
      { retries: 2, timeoutMs: 12_000 },
    );
    if (!stepsRes.ok) throw new Error(`fitbit ${stepsRes.status}`);
    const stepsJson = (await stepsRes.json()) as { 'activities-steps'?: readonly { dateTime: string; value: string }[] };
    const days: FitbitDay[] = [];
    for (const s of stepsJson['activities-steps'] ?? []) {
      days.push({
        date: s.dateTime,
        steps: Number(s.value),
        restingHeartRate: 0,
        sleepMinutes: 0,
        sleepEfficiency: 0,
        weightKg: null,
      });
    }
    if (days.length === 0) {
      days.push({ date: today, steps: 0, restingHeartRate: 0, sleepMinutes: 0, sleepEfficiency: 0, weightKg: null });
    }
    return days;
  }

  mockPayload(): FitbitPayload {
    const today = new Date();
    const days: FitbitDay[] = [];
    for (let d = 29; d >= 0; d--) {
      const date = new Date(today);
      date.setDate(today.getDate() - d);
      const v = (Math.cos(d * 0.4) + 1) * 0.5;
      days.push({
        date: date.toISOString().slice(0, 10),
        steps: 7200 + Math.round(v * 4500),
        restingHeartRate: 60 + Math.round(v * 6),
        sleepMinutes: 390 + Math.round(v * 90),
        sleepEfficiency: 86 + Math.round(v * 8),
        weightKg: 72.4,
      });
    }
    return { daysTrailing30: days, weeklyHrvMs: 56 };
  }
}
